import { ContentDelete, SocialMessage, ContentDownload } from '@fs/zion-icon/dist/cjs/icons'
import { useHistory } from '@fs/zion-router'
import { Button, DangerButton, IconButton, ResponsiveTableRow, Row, Select, useOverlay, H5 } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getSessionId } from '@fs/zion-session'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import { downloadFile } from '../../../util/FileDownloadUtil'
import useNotifications from '../../../hooks/useNotifications'
import {
  getAttachedImage,
  getChannelChatMessages,
  getMessageAttachmentInfo,
  getReplyChatMessages,
} from '../../../services/chatService'
import { convertUTCString } from '../../../util/DateUtil'
import { getIdElement } from '../../../util/StringUtil'
import { ChatLink, CisLink } from '../../app/Links'
import SortableColumnHeader from '../../common/SortableColumnHeader'
import { ChatContext } from '../ChatContextProvider'
import RedactSelectedMessagesOverlay from '../common/overlays/RedactSelectedMessagesOverlay'
import RedactSingleMessageOverlay from '../common/overlays/RedactSingleMessageOverlay'
import ChatMessage from '../common/table/ChatMessage'
import ReplyTable from '../common/table/ReplyTable'
import { focusedMessageStyles } from '../common/styles'
import messageLogicOperators from '../common/enums'
import PreviousNext from '../common/table/PreviousNext'
import DownloadAttachmentsOverlay from '../common/overlays/DownloadAttachmentsOverlay'
import { addArtifactsToZip } from '../../artifacts/util/downloadUtils'
import { getArtifact } from '../../../services/artifactService'

const CHAT_MESSAGES_RESULTS_PER_PAGE_PREF_KEY = 'dmc.chatmessages.resultsperpage'

export default function ChatMessages() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { chat, parentMessage } = useContext(ChatContext)
  const redactSingleMessageOverlay = useOverlay()
  const redactSelectedMessagesOverlay = useOverlay()
  const downloadAttachmentsOverlay = useOverlay()
  const [resultsPerPage, setResultsPerPage] = useState(
    localStorage.getItem(CHAT_MESSAGES_RESULTS_PER_PAGE_PREF_KEY) || '30'
  )
  const [selectedMessages, setSelectedMessages] = useState([])
  const [allSelectedMessages, setAllSelectedMessages] = useState([])
  const [selectedMessage, setSelectedMessage] = useState()
  const [hasNewerMessages, setHasNewerMessages] = useState(false)
  const [hasOlderMessages, setHasOlderMessages] = useState(false)
  const [channelMessages, setChannelMessages] = useState()
  const [channelMessagesReplyThreads, setChannelMessagesReplyThreads] = useState({})
  const [sortField, setSortField] = useState()
  const [sortType, setSortType] = useState()
  const sortedChannelMessages = sortChannelMessages()
  const [expandedMessages, setExpandedMessages] = useState({})
  const [showingReplies, setShowingReplies] = useState({})
  const [scrolledToMessage, setScrolledToMessage] = useState(false)
  const [fetchingImages, setFetchingImages] = useState()
  const [redactedMessagesList, setRedactedMessagesList] = useState([])
  const [messagesToDownload, setMessagesToDownload] = useState([])
  const [repliesToDownload, setRepliesToDownload] = useState([])
  const [attachmentsToDownload, setAttachmentsToDownload] = useState([])
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const parentMessageId = searchParams.get('parentMessageId')
  const messageId = searchParams.get('messageId')
  const focusedMessageIdRef = useRef()
  const resultsPerPageAsInt = parseInt(resultsPerPage, 10)
  const totalParentMessagesOnScreen = channelMessages?.length
  const totalReplyMessagesOnScreen = useMemo(
    () =>
      Object.values(channelMessagesReplyThreads)?.reduce(
        (numOfReplyMessages, channel) => numOfReplyMessages + channel.messages.length,
        0
      ),
    [channelMessagesReplyThreads]
  )
  const allMessagesExpanded =
    Object.keys(expandedMessages).length ===
    totalParentMessagesOnScreen + totalReplyMessagesOnScreen + (parentMessage ? 1 : 0)
  const hasImageAttachments = useMemo(
    () =>
      sortedChannelMessages?.some((message) =>
        message.attachments?.some((attachment) => attachment.serviceType === 'artifact_store')
      ),
    [sortedChannelMessages]
  )

  useEffect(() => {
    const getChannelChatMessagesAsync = async () => {
      try {
        if (messageId && !scrolledToMessage && chat.cid) {
          // eslint-disable-next-line prettier/prettier
          const olderThanMessagesLimit = Math.ceil(resultsPerPageAsInt / 2) + 1
          const olderThanMessagesAndMessageResponse = await getChannelChatMessages(
            chat.cid,
            olderThanMessagesLimit,
            parentMessageId || messageId,
            messageLogicOperators.OLDER_THAN_OR_EQUAL
          )
          if (olderThanMessagesAndMessageResponse.data.messages.length === olderThanMessagesLimit) {
            setHasOlderMessages(true)
            olderThanMessagesAndMessageResponse.data.messages.shift()
          } else {
            setHasOlderMessages(false)
          }
          const newerThanMessagesLimit =
            resultsPerPageAsInt - olderThanMessagesAndMessageResponse.data.messages.length + 1
          const newerThanMessagesResponse = await getChannelChatMessages(
            chat.cid,
            newerThanMessagesLimit,
            parentMessageId || messageId,
            messageLogicOperators.NEWER_THAN
          )
          if (newerThanMessagesResponse.data.messages.length === newerThanMessagesLimit) {
            setHasNewerMessages(true)
            newerThanMessagesResponse.data.messages.pop()
          } else {
            setHasNewerMessages(false)
          }
          setChannelMessages([
            ...olderThanMessagesAndMessageResponse.data.messages,
            ...newerThanMessagesResponse.data.messages,
          ])
        } else if (!messageId && !scrolledToMessage && chat.cid) {
          if (chat.type === 'fs_thread') {
            const response = await getReplyChatMessages(chat.cid, resultsPerPageAsInt + 1)
            if (response.data.messages.length === resultsPerPageAsInt + 1) {
              setHasNewerMessages(true)
              response.data.messages.pop()
            }
            setChannelMessages(response.data.messages)
          } else {
            const response = await getChannelChatMessages(chat.cid, resultsPerPageAsInt + 1, null, null)
            if (response.data.messages.length === resultsPerPageAsInt + 1) {
              setHasOlderMessages(true)
              response.data.messages.shift()
            } else {
              setHasOlderMessages(false)
            }
            setChannelMessages(response.data.messages)
          }
        } else if (!chat.cid) {
          setChannelMessages([])
        }
      } catch (err) {
        setChannelMessages([])
        error(
          `${t('user.messages.errorFetching', {
            defaultValue: 'Error getting user messages',
          })}${err.message ? `: ${err.message}` : ''}`,
          err
        )
      }
    }
    getChannelChatMessagesAsync()
  }, [
    error,
    resultsPerPage,
    t,
    chat.cid,
    messageId,
    scrolledToMessage,
    resultsPerPageAsInt,
    parentMessageId,
    chat.type,
  ])

  useEffect(() => {
    const scrollToMessage = async () => {
      if (!scrolledToMessage && channelMessages && messageId && focusedMessageIdRef.current) {
        if (hasImageAttachments) {
          if (fetchingImages && fetchingImages.length === 0) {
            focusedMessageIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            setScrolledToMessage(true)
            document.getElementById('rowToOpen').click()
          }
        } else {
          focusedMessageIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setScrolledToMessage(true)
          document.getElementById('rowToOpen').click()
        }
      }
    }
    scrollToMessage()
  }, [channelMessages, parentMessageId, messageId, scrolledToMessage, fetchingImages, hasImageAttachments])

  const saveImage = async (zip, imageSource, id) => {
    await getAttachedImage(imageSource)
      .then((response) => {
        zip.file(`${id}.jpg`, response.data)
      })
      .catch((err) => {
        error(t('chat.errorFetchingImageSource', 'Error fetching image source'), err)
      })
  }

  const saveAttachments = async (zip) => {
    const artifacts = []
    await Promise.all(
      attachmentsToDownload.map(async (attachment) => {
        if (attachment.serviceType === 'artifact_store') {
          await getMessageAttachmentInfo(attachment.id)
            .then(async (attachmentInfo) => {
              await saveImage(zip, `${attachmentInfo.data.thumbmobileUri}?sessionId=${getSessionId()}`, attachment.id)
            })
            .catch((err) => {
              error(t('chat.errorGettingMessageAttachmentInfo', 'Error getting attachments for messages'), err)
            })
        }
        if (attachment.serviceType === 'memory_artifact') {
          await getArtifact(attachment.id)
            .then((response) => {
              artifacts.push(response.data)
            })
            .catch((err) => {
              error(t('chat.errorGettingArtifact', 'Error getting attached artifacts'), err)
            })
        }
      })
    )
    await addArtifactsToZip(artifacts, zip)
  }

  const downloadWithoutAttachments = async (messages, replies) => {
    let csvString = `${t('time', 'Time')},${t('user', 'User')},${t('message', 'Message')}\n`
    const messagesResult = messages?.length > 0 ? messages : messagesToDownload
    const repliesResult = replies && Object.entries(replies)?.length > 0 ? replies : repliesToDownload
    messagesResult.forEach(async (message) => {
      csvString += `${message.updatedAtDate},${message.createdByName},"${message.text}"\n`
      if (message.replyCid) {
        repliesResult[message.replyCid].forEach((reply) => {
          csvString += `${reply.updatedAtDate},${reply.createdByName},"${reply.text}"\n`
        })
      }
    })

    const csvContent = `\uFEFF${csvString}`
    downloadFile(csvContent, `${t('chat.messages.fileName', 'chat_messages')}.csv`, 'text/csv;charset=utf-8;')
  }

  const downloadWithAttachments = async () => {
    downloadAttachmentsOverlay.close()
    let csvString = `${t('time', 'Time')},${t('user', 'User')},${t('message', 'Message')},${t(
      'attachments',
      'Attachments'
    )}\n`
    const zip = new JSZip()
    await saveAttachments(zip)

    messagesToDownload.forEach(async (message) => {
      const attachmentIds = []
      message.attachments?.forEach((attachment) => {
        attachmentIds.push(attachment.id)
      })
      csvString += `${message.updatedAtDate},${message.createdByName},"${message.text}","${attachmentIds.join(', ')}"\n`
      if (message.replyCid) {
        repliesToDownload[message.replyCid].forEach((reply) => {
          csvString += `${reply.updatedAtDate},${reply.createdByName},"${reply.text}","${reply.attachmentIds.join(
            ', '
          )}"\n`
        })
      }
    })

    const file = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' })
    zip.file(`${t('chat.messages.fileName', 'chat_messages')}.csv`, file)

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    saveAs(zipBlob, `${t('chat.messages.fileName', 'chat_messages')}.zip`)
  }

  const getReplyThreadForDownload = async (replies, message, attachments) => {
    replies[message.replyCid] = []
    await getChannelChatMessages(message.replyCid, null, null, null, true)
      .then((repliesResponse) => {
        repliesResponse.data.messages.forEach((reply) => {
          const attachmentIds = []
          reply.attachments?.forEach(async (attachment) => {
            attachments.push(attachment)
            attachmentIds.push(attachment.id)
          })
          replies[message.replyCid].push({
            updatedAtDate: reply.updatedAtDate,
            createdByName: reply.createdByName,
            text: reply.text,
            attachmentIds,
          })
        })
      })
      .catch((err) => {
        error(
          t('chat.errorGettingChannelChatMessagesReplies', 'Error getting reply threads for channel chat messages'),
          err
        )
      })
  }

  const prepDownload = async () => {
    let messages = []
    const attachments = []
    const replies = {}

    if (messagesToDownload.length === 0) {
      await getChannelChatMessages(chat.cid, null, null, null, true)
        .then(async (response) => {
          messages = response.data.messages
          await Promise.all(
            response.data.messages.map(async (message) => {
              message.attachments?.forEach((attachment) => {
                attachments.push(attachment)
              })
              if (message.replyCid) {
                await getReplyThreadForDownload(replies, message, attachments)
              }
            })
          )
          setMessagesToDownload(response.data.messages)
          setAttachmentsToDownload(attachments)
          setRepliesToDownload(replies)
        })
        .catch((err) => {
          error(t('chat.errorGettingChannelChatMessages', 'Error getting channel chat messages'), err)
        })
    }

    if (attachments.length > 0 || attachmentsToDownload.length > 0) {
      downloadAttachmentsOverlay.handleClick()
    } else {
      await downloadWithoutAttachments(messages, replies)
    }
  }

  const onPrevious = async () => {
    history.location.search && history.replace(history.location.pathname)
    const response = await getChannelChatMessages(
      chat.cid,
      resultsPerPageAsInt + 1,
      channelMessages[0].messageId,
      messageLogicOperators.OLDER_THAN
    )
    if (response.data.messages.length !== resultsPerPageAsInt + 1) {
      setHasOlderMessages(false)
    } else {
      response.data.messages.shift()
    }
    setHasNewerMessages(true)
    setChannelMessages(response.data.messages)
  }

  const onNext = async () => {
    history.location.search && history.replace(history.location.pathname)
    const response = await getChannelChatMessages(
      chat.cid,
      resultsPerPageAsInt + 1,
      channelMessages[channelMessages.length - 1].messageId,
      messageLogicOperators.NEWER_THAN
    )
    if (response.data.messages.length !== resultsPerPageAsInt + 1) {
      setHasNewerMessages(false)
    } else {
      response.data.messages.pop()
    }
    setHasOlderMessages(true)
    setChannelMessages(response.data.messages)
  }

  const redactSingleOnClick = (mId) => {
    setSelectedMessage(mId)
    redactSingleMessageOverlay.open()
  }

  const updateRedactedMessagesList = (newlyRedactedMessages) => {
    setRedactedMessagesList((prevState) => [...prevState, ...newlyRedactedMessages])
  }

  const fetchReplies = async (channelMessage) => {
    try {
      let hasNewerReplies = false
      let hasOlderReplies = false
      if (parentMessageId && parentMessageId === channelMessage.messageId) {
        const olderThanMessagesLimit = Math.ceil(resultsPerPageAsInt / 2) + 1
        const olderThanMessagesAndMessageResponse = await getChannelChatMessages(
          channelMessage.replyCid,
          olderThanMessagesLimit,
          messageId,
          messageLogicOperators.OLDER_THAN_OR_EQUAL
        )
        if (olderThanMessagesAndMessageResponse.data.messages.length === olderThanMessagesLimit) {
          hasOlderReplies = true
          olderThanMessagesAndMessageResponse.data.messages.shift()
        }
        const newerThanMessagesLimit =
          resultsPerPageAsInt - olderThanMessagesAndMessageResponse.data.messages.length + 1
        const newerThanMessagesResponse = await getChannelChatMessages(
          channelMessage.replyCid,
          newerThanMessagesLimit,
          messageId,
          messageLogicOperators.NEWER_THAN
        )
        if (newerThanMessagesResponse.data.messages.length === newerThanMessagesLimit) {
          hasNewerReplies = true
          newerThanMessagesResponse.data.messages.pop()
        }
        const combinedMessages = [
          ...olderThanMessagesAndMessageResponse.data.messages,
          ...newerThanMessagesResponse.data.messages,
        ]
        setChannelMessagesReplyThreads((prevState) => {
          return {
            ...prevState,
            [channelMessage.replyCid]: {
              ...newerThanMessagesResponse.data,
              messages: combinedMessages,
              returnedCount: combinedMessages.length,
              hasNewerReplies,
              hasOlderReplies,
            },
          }
        })
      } else {
        const response = await getReplyChatMessages(channelMessage.replyCid, resultsPerPageAsInt + 1)
        if (response.data.messages.length === resultsPerPageAsInt + 1) {
          response.data.messages.pop()
          hasNewerReplies = true
        }
        setChannelMessagesReplyThreads((prevState) => {
          return {
            ...prevState,
            [channelMessage.replyCid]: {
              ...response.data,
              hasNewerReplies,
            },
          }
        })
      }
    } catch (err) {
      error(
        `${t('chat.errorFetchingReplies', {
          defaultValue: 'Something went wrong fetching replies',
        })}${err.message ? `: ${err.message}` : ''}`,
        err
      )
    }
  }

  const onMoreLessClick = useCallback((mId) => {
    setExpandedMessages((prevState) => {
      if (prevState[mId]) {
        const expandedMessagesCopy = { ...prevState }
        delete expandedMessagesCopy[mId]
        return expandedMessagesCopy
      }
      return { ...prevState, [mId]: true }
    })
  }, [])

  const resultsPerPageOnChange = async (e) => {
    setResultsPerPage(e.target.value)
    localStorage.setItem(CHAT_MESSAGES_RESULTS_PER_PAGE_PREF_KEY, e.target.value)
    try {
      const newResultsPerPageAsInt = parseInt(e.target.value, 10)
      const response = await getChannelChatMessages(chat.cid, newResultsPerPageAsInt + 1, null, null)
      if (response.data.messages.length === newResultsPerPageAsInt + 1) {
        setHasOlderMessages(true)
        response.data.messages.shift()
      } else {
        setHasOlderMessages(false)
      }
      setChannelMessages(response.data.messages)
    } catch (err) {
      setChannelMessages([])
      error(
        `${t('user.messages.errorFetching', {
          defaultValue: 'Error getting user messages',
        })}${err.message ? `: ${err.message}` : ''}`,
        err
      )
    }
  }

  const rowOnClickOrExpand = (channelMessage) => {
    if (!channelMessagesReplyThreads[channelMessage.replyCid]) {
      fetchReplies(channelMessage)
    }
    if (showingReplies[channelMessage.messageId]) {
      setShowingReplies((prevState) => {
        const showingRepliesCopy = { ...prevState }
        delete showingRepliesCopy[channelMessage.messageId]
        return showingRepliesCopy
      })
    } else {
      setShowingReplies((prevState) => {
        return { ...prevState, [channelMessage.messageId]: true }
      })
    }
  }

  function sortChannelMessages() {
    const getItem = (item, arr) => {
      if (arr.length === 0) {
        return item
      }
      return getItem(item[arr[0]], arr.slice(1))
    }
    const normalSort = (fields) => {
      switch (sortType) {
        case undefined: {
          return [...channelMessages]
        }
        case 'ascending': {
          return [...channelMessages].sort((a, b) => {
            const itemA = getItem(a, fields)
            const itemB = getItem(b, fields)
            return (itemA || '').localeCompare(itemB || '')
          })
        }
        case 'descending': {
          return [...channelMessages].sort((a, b) => {
            const itemA = getItem(a, fields)
            const itemB = getItem(b, fields)
            return (itemB || '').localeCompare(itemA || '')
          })
        }
        default:
          break
      }
      return [...channelMessages]
    }
    const dateSort = (field) => {
      switch (sortType) {
        case undefined: {
          return [...channelMessages]
        }
        case 'ascending': {
          return [...channelMessages].sort((a, b) => {
            const aDate = Date.parse(a[field])
            const bDate = Date.parse(b[field])
            if (a[field] === undefined) {
              return 1
            }
            if (b[field] === undefined) {
              return -1
            }
            if (aDate === bDate) {
              return 0
            }
            return aDate < bDate ? -1 : 1
          })
        }
        case 'descending': {
          return [...channelMessages].sort((a, b) => {
            const aDate = Date.parse(a[field])
            const bDate = Date.parse(b[field])
            if (a[field] === undefined) {
              return 1
            }
            if (b[field] === undefined) {
              return -1
            }
            if (aDate === bDate) {
              return 0
            }
            return aDate < bDate ? 1 : -1
          })
        }
        default:
          break
      }
      return [...channelMessages]
    }
    if (channelMessages) {
      switch (sortField) {
        case 'updatedAtDate': {
          return dateSort('updatedAtDate')
        }
        case 'deletedAtDate': {
          return dateSort('deletedAtDate')
        }
        case 'createdByName': {
          return normalSort(['createdByName'])
        }
        default:
          break
      }
    }
    return channelMessages
  }

  const sortIconOnClick = (type, field) => {
    setSortType(type)
    setSortField(field)
  }

  const sortByFieldType = (field) => {
    if (field !== sortField) {
      sortIconOnClick('ascending', field)
    } else if (sortType === undefined) {
      sortIconOnClick('ascending', field)
    } else if (sortType === 'ascending') {
      sortIconOnClick('descending', field)
    } else if (sortType === 'descending') {
      sortIconOnClick(undefined, undefined)
    }
  }

  const appendSelectedMessages = useCallback((messagesToAdd) => {
    setAllSelectedMessages((prevState) => [...prevState, ...messagesToAdd])
  }, [])

  const removeSelectedMessages = useCallback((messagesToRemove) => {
    setAllSelectedMessages((prevState) => prevState.filter((mId) => !messagesToRemove.includes(mId)))
  }, [])

  const setSelectedMessagesAndAllMessages = useCallback(
    (messages) => {
      if (messages.length === selectedMessages.length) {
        return
      }
      if (messages.length < selectedMessages.length) {
        const messagesToRemove = selectedMessages.filter((message) => !messages.includes(message))
        removeSelectedMessages(messagesToRemove)
      } else {
        const messagesToAppend = messages.filter((message) => !selectedMessages.includes(message))
        appendSelectedMessages(messagesToAppend)
      }
      setSelectedMessages(messages)
    },
    [appendSelectedMessages, removeSelectedMessages, selectedMessages]
  )

  return (
    <>
      {sortedChannelMessages && (
        <>
          {sortedChannelMessages.length > 0 && (
            <>
              <Row alignY="middle">
                <Select
                  id="resultsPerPage"
                  label={t('resultsPerPage', 'Results per page')}
                  hidePlaceholder
                  options={[
                    { value: '30', label: '30' },
                    { value: '60', label: '60' },
                    { value: '90', label: '90' },
                    { value: '120', label: '120' },
                    { value: '150', label: '150' },
                  ]}
                  value={resultsPerPage}
                  onChange={resultsPerPageOnChange}
                />
                <PreviousNext
                  growX
                  direction="vertical"
                  onPrevious={onPrevious}
                  onNext={onNext}
                  disablePrevious={!hasOlderMessages}
                  disableNext={!hasNewerMessages}
                />
                <DownloadAttachmentsOverlay
                  downloadWithAttachments={downloadWithAttachments}
                  downloadWithoutAttachments={downloadWithoutAttachments}
                  {...downloadAttachmentsOverlay}
                />
                <Button emphasis="medium" Icon={ContentDownload} onClick={prepDownload}>
                  {t('chat.download', 'Download Chat')}
                </Button>
                <Button
                  emphasis="medium"
                  Icon={SocialMessage}
                  onClick={() =>
                    setExpandedMessages(() => {
                      if (allMessagesExpanded) {
                        return {}
                      }
                      const newExpandedMessages = {}
                      sortedChannelMessages.forEach((channelMessage) => {
                        newExpandedMessages[channelMessage.messageId] = true
                      })
                      Object.values(channelMessagesReplyThreads).forEach((channel) => {
                        channel.messages.forEach((message) => {
                          newExpandedMessages[`${message.messageId}`] = true
                        })
                      })
                      if (parentMessage) {
                        newExpandedMessages[parentMessage.messageId] = true
                      }
                      return newExpandedMessages
                    })
                  }
                >
                  {allMessagesExpanded ? t('collapseAll', 'Collapse All') : t('expandAll', 'Expand All')}
                </Button>
                {allSelectedMessages.length > 0 && (
                  <>
                    <DangerButton onClick={redactSelectedMessagesOverlay.handleClick}>
                      {t('redactSelected', 'Redact Selected')}
                    </DangerButton>
                    <RedactSelectedMessagesOverlay
                      updateRedactedMessagesList={updateRedactedMessagesList}
                      selectedMessages={allSelectedMessages}
                      {...redactSelectedMessagesOverlay}
                    />
                  </>
                )}
              </Row>
              <RedactSingleMessageOverlay
                selectedMessage={selectedMessage}
                updateRedactedMessagesList={updateRedactedMessagesList}
                {...redactSingleMessageOverlay}
              />
              <ParentMessage
                appendSelectedMessages={appendSelectedMessages}
                removeSelectedMessages={removeSelectedMessages}
                expandedMessages={expandedMessages}
                onMoreLessClick={onMoreLessClick}
                redactSingleOnClick={redactSingleOnClick}
              />
              <DmcResponsiveCheckboxTable
                id="Chat Message Table"
                totalColumnCount={5}
                onSelectionChange={setSelectedMessagesAndAllMessages}
                atSizeColumns={{
                  default: {
                    labels: [
                      t('message', 'Message'),
                      t('id', 'ID'),
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="createdByName"
                        onClick={() => sortByFieldType('createdByName')}
                        label={t('user', 'User')}
                        block
                      />,
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="updatedAtDate"
                        onClick={() => sortByFieldType('updatedAtDate')}
                        label={t('updated', 'Updated')}
                        block
                      />,
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="deletedAtDate"
                        onClick={() => sortByFieldType('deletedAtDate')}
                        label={`${t('redacted', 'Redacted')}/${t('deleted', 'Deleted')}`}
                        block
                      />,
                    ],
                    columnWidths: [4, 2, 2, 2, 2],
                  },
                }}
              >
                {sortedChannelMessages.map((channelMessage) => {
                  const shouldFocusMessageId = channelMessage.messageId === messageId
                  const messageToFocusIsReply = channelMessage.messageId === parentMessageId
                  let shouldShowFocusMessageCss = false
                  if (shouldFocusMessageId) {
                    if (hasImageAttachments) {
                      shouldShowFocusMessageCss = fetchingImages && fetchingImages.length === 0
                    } else {
                      shouldShowFocusMessageCss = true
                    }
                  }
                  return (
                    <ResponsiveTableRow
                      key={channelMessage.messageId}
                      id={shouldFocusMessageId || messageToFocusIsReply ? 'rowToOpen' : undefined}
                      onClick={channelMessage.replyCount ? () => rowOnClickOrExpand(channelMessage) : undefined}
                      onExpandToggle={channelMessage.replyCount ? () => rowOnClickOrExpand(channelMessage) : undefined}
                      selected={showingReplies[channelMessage.messageId]}
                      name={channelMessage.messageId}
                      disabled={
                        channelMessage.deletedAtDate || redactedMessagesList.includes(channelMessage.messageId)
                          ? {
                              Icon: ContentDelete,
                            }
                          : undefined
                      }
                      column1={
                        <ChatMessage
                          setFetchingImages={setFetchingImages}
                          message={channelMessage}
                          showMore={expandedMessages[channelMessage.messageId]}
                          onMoreLessClick={() => onMoreLessClick(channelMessage.messageId)}
                        />
                      }
                      column2={
                        <div
                          css={shouldShowFocusMessageCss && focusedMessageStyles}
                          ref={shouldFocusMessageId || messageToFocusIsReply ? focusedMessageIdRef : undefined}
                        >
                          {channelMessage.messageId}
                        </div>
                      }
                      column3={
                        <span>
                          <CisLink
                            userId={getIdElement(channelMessage.createdByCisId)}
                            userName={channelMessage.createdByName}
                          />
                        </span>
                      }
                      column4={channelMessage.updatedAtDate && convertUTCString(channelMessage.updatedAtDate, true)}
                      column5={
                        channelMessage.deletedAtDate ? (
                          convertUTCString(channelMessage.deletedAtDate, true)
                        ) : (
                          <IconButton
                            data-testid="delete message button"
                            Icon={ContentDelete}
                            onClick={() => redactSingleOnClick(channelMessage.messageId)}
                            color="red"
                          />
                        )
                      }
                    >
                      {channelMessage.replyCid && (
                        <>
                          {channelMessagesReplyThreads[channelMessage.replyCid] && (
                            <ReplyTable
                              messageIdToFocus={parentMessageId && messageId}
                              redactedMessagesList={redactedMessagesList}
                              replyChannel={channelMessagesReplyThreads[channelMessage.replyCid]}
                              setChannelMessagesReplyThreads={setChannelMessagesReplyThreads}
                              expandedMessages={expandedMessages}
                              onMoreLessClick={onMoreLessClick}
                              redactSingleOnClick={redactSingleOnClick}
                              appendSelectedMessages={appendSelectedMessages}
                              removeSelectedMessages={removeSelectedMessages}
                              resultsPerPage={resultsPerPageAsInt}
                            />
                          )}
                        </>
                      )}
                    </ResponsiveTableRow>
                  )
                })}
              </DmcResponsiveCheckboxTable>
            </>
          )}
          {channelMessages.length === 0 && <H5 supplementary>{t('chat.messages.noMessages', 'No messages found')}</H5>}
        </>
      )}
    </>
  )
}

function ParentMessage({
  appendSelectedMessages,
  removeSelectedMessages,
  expandedMessages,
  onMoreLessClick,
  redactSingleOnClick,
}) {
  const { t } = useTranslation()
  const [selectedMessages, setSelectedMessages] = useState([])
  const { parentMessage } = useContext(ChatContext)
  const setSelectedMessagesAndAllMessages = useCallback(
    (messages) => {
      if (messages.length === selectedMessages.length) {
        return
      }
      if (messages.length < selectedMessages.length) {
        const messagesToRemove = selectedMessages.filter((message) => !messages.includes(message))
        removeSelectedMessages(messagesToRemove)
      } else {
        const messagesToAppend = messages.filter((message) => !selectedMessages.includes(message))
        appendSelectedMessages(messagesToAppend)
      }
      setSelectedMessages(messages)
    },
    [appendSelectedMessages, removeSelectedMessages, selectedMessages]
  )

  return (
    <>
      {parentMessage && (
        <>
          <H5>{t('chat.parentMessage', 'Parent Message')}</H5>
          <DmcResponsiveCheckboxTable
            id="Parent Message Table"
            totalColumnCount={5}
            onSelectionChange={setSelectedMessagesAndAllMessages}
            atSizeColumns={{
              default: {
                labels: [
                  t('message', 'Message'),
                  t('id', 'ID'),
                  t('user', 'User'),
                  t('updated', 'Updated'),
                  `${t('redacted', 'Redacted')}/${t('deleted', 'Deleted')}`,
                ],
                columnWidths: [4, 2, 2, 2, 2],
              },
            }}
          >
            <ResponsiveTableRow
              name={parentMessage.messageId}
              disabled={
                parentMessage.deletedAtDate && {
                  Icon: ContentDelete,
                }
              }
              column1={
                <ChatMessage
                  message={parentMessage}
                  showMore={expandedMessages[parentMessage.messageId]}
                  onMoreLessClick={() => onMoreLessClick(parentMessage.messageId)}
                />
              }
              column2={
                <span style={{ wordWrap: 'break-word' }}>
                  <ChatLink
                    channelId={parentMessage.cid}
                    text={parentMessage.messageId}
                    messageId={parentMessage.messageId}
                  />
                </span>
              }
              column3={
                <span>
                  <CisLink userId={getIdElement(parentMessage.createdByCisId)} userName={parentMessage.createdByName} />
                </span>
              }
              column4={parentMessage.updatedAtDate && convertUTCString(parentMessage.updatedAtDate, true)}
              column5={
                parentMessage.deletedAtDate ? (
                  convertUTCString(parentMessage.deletedAtDate, true)
                ) : (
                  <IconButton
                    Icon={ContentDelete}
                    onClick={() => redactSingleOnClick(parentMessage.messageId)}
                    color="red"
                  />
                )
              }
            />
          </DmcResponsiveCheckboxTable>
          <H5>{t('chat.replies', 'Replies')}</H5>
        </>
      )}
    </>
  )
}
